import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { oauthRedirectUrl } from '../lib/auth';
import { POST_LOGIN_KEY, rememberTarget } from '../lib/postLogin';
import { buttonClasses } from '@payforge/ui';
import payforgeWordmark from '../assets/payforge-wordmark.svg';

// Exact strings per the #869dbezdd spec "Error Message Copy (final)". Do not paraphrase.
const ERROR_MESSAGES: Record<string, string> = {
  domain_not_allowed:
    'PayforgeHR is only available to PayForge employees. Please sign in with your @payforge.ai account.',
  not_provisioned:
    "Your account hasn't been set up in PayforgeHR yet. Please contact your HR administrator at hr@payforge.ai.",
  access_not_started:
    "Your access to PayforgeHR hasn't started yet. You can sign in from your hire date.",
  access_ended:
    'Your access to PayforgeHR has ended. Please contact HR at hr@payforge.ai if you need assistance.',
  account_inactive: 'Your account is inactive. Please contact HR at hr@payforge.ai.',
  login_disabled:
    'Your account is not available for sign-in right now. Please contact HR at hr@payforge.ai.',
  google_failure:
    "We couldn't sign you in. Please try again, or contact HR if the problem persists.",
};

const SECURITY_REASON_MESSAGE =
  "You've been signed out for security reasons. Please sign in again.";

export function Login() {
  const [params] = useSearchParams();
  const errorCode = params.get('error');
  const reason = params.get('reason');
  const message =
    reason === 'security'
      ? SECURITY_REASON_MESSAGE
      : (errorCode && ERROR_MESSAGES[errorCode]) || null;

  // The page to return to after sign-in (lib/postLogin). A safe `next` is kept, which also covers a
  // /login?next= link opened in a new tab. Without one the kept page is dropped (a deliberate
  // sign-out, a typed /login, an unsafe next), except after a failed sign-in (`error`), so a retry
  // still returns to it.
  const next = params.get('next');
  useEffect(() => {
    const kept = next === null ? null : rememberTarget(next);
    if (kept === null && errorCode === null) {
      try {
        window.sessionStorage.removeItem(POST_LOGIN_KEY);
      } catch {
        // Storage blocked: nothing was kept.
      }
    }
  }, [next, errorCode]);

  // Gentle magnetic pull on the decorative glows. Writes the normalized cursor
  // offset (-0.5..0.5) to CSS vars both clouds read; each cloud eases toward the
  // cursor. Skipped under prefers-reduced-motion.
  const glowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = glowRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    let frame = 0;
    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        el.style.setProperty('--px', (event.clientX / window.innerWidth - 0.5).toFixed(3));
        el.style.setProperty('--py', (event.clientY / window.innerHeight - 0.5).toFixed(3));
      });
    };
    window.addEventListener('pointermove', onMove);
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-slate-50 px-4">
      {/* Soft brand glows behind the card. Decorative only; drift with the cursor. */}
      <div ref={glowRef} aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -left-[8vw] -top-[8vw] h-[36vw] min-h-72 w-[36vw] min-w-72 rounded-full bg-brand-200/25 blur-3xl transition-transform duration-700 ease-out"
          style={{ transform: 'translate3d(calc(var(--px, 0) * 60px), calc(var(--py, 0) * 60px), 0)' }}
        />
        <div
          className="absolute -bottom-[8vw] -right-[8vw] h-[36vw] min-h-72 w-[36vw] min-w-72 rounded-full bg-accent-200/25 blur-3xl transition-transform duration-700 ease-out"
          style={{ transform: 'translate3d(calc(var(--px, 0) * 48px), calc(var(--py, 0) * 48px), 0)' }}
        />
      </div>

      <section className="relative flex w-full max-w-md flex-col items-center gap-8 rounded-[14px] bg-surface p-12">
        {/* Two-tone wordmark per the design: black "payforge" + grey "HR". */}
        <div className="flex items-center justify-center gap-2">
          {/* The wordmark asset is a single flat #000000 path, so it disappears on a dark
              surface. It is an <img>, which CSS cannot recolour - but because it is pure black
              on transparent, `invert` maps it to pure white exactly. Cheaper and less
              drift-prone than shipping a second asset and swapping on the resolved theme. */}
          <img src={payforgeWordmark} alt="Payforge" className="h-9 w-auto dark:invert" />
          <span className="text-[32px] font-medium leading-none tracking-tight text-slate-700">
            HR
          </span>
        </div>

        <div className="flex w-full flex-col gap-3">
          {message ? (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
            >
              {message}
            </div>
          ) : null}

          {/* Google sign-in. Uses the app's canonical pill button for consistency. */}
          <a href={oauthRedirectUrl} className={buttonClasses('primary', 'lg', { className: 'w-full gap-3' })}>
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
              <path
                fill="#ffffff"
                d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"
              />
            </svg>
            Sign in with Google
          </a>

          {/* or divider */}
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-sm text-slate-500">or</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          {/* PayforgeHR uses Google SSO only (no former-employee login) — help link in its place */}
          <p className="text-center text-sm">
            <a className="font-medium text-slate-900 hover:underline" href="mailto:hr@payforge.ai">
              Need help signing in?
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}
